import { httpDelete, httpGet, httpPost } from "@/api/connection";
import type { UploaderApi } from "@/Features/Files/Uploader";

import type {
  CompleteUploadBody,
  CompleteUploadResult,
  DeleteStoragePhotoData,
  GetStoragePhotosQuery,
  GetStoragePhotosResult,
  StartUploadBody,
  StartUploadResult,
} from "./storage-photos.types";

/**
 * connection.js `handleError` resolves failures as `{ success: false, error }`
 * instead of rejecting, so callers (react-query, the Uploader) would treat
 * them as data. Reject here, keeping the server's `message` (413 storage
 * limit, 400 allowlist, 409 duplicate) so the upload tile can show it.
 *
 * The axios interceptor already toasts HTTP failures once per request; this
 * layer and the Photos tab must not add a second snackbar. A 200 body with
 * `success: false` or missing `data` is rejected here without a toast, so
 * consumers should surface `err.message` for those.
 *
 * `requireData` is false for DELETE, which returns an empty 200.
 */
const assertOk = <T>(
  res: any,
  fallbackMessage: string,
  requireData = true
): T => {
  if (res?.success === false || (requireData && res?.data == null)) {
    // Failures arrive as { success: false, error } (HTTP error) or, for a
    // 200 body that itself says success: false, with a top-level message.
    const message = res?.error?.response?.data?.message ?? res?.message;
    throw new Error(
      typeof message === "string" && message ? message : fallbackMessage
    );
  }
  return res as T;
};

// Function and type names mirror monolith-api src/resources/storage-photos
// (StoragePhotosService.get, startUpload, completeUpload, delete).
const base = (storage_uuid: string) => `/storage/${storage_uuid}/photos`;

const get = async (
  storage_uuid: string,
  params: GetStoragePhotosQuery = {}
): Promise<GetStoragePhotosResult> => {
  const res = await httpGet(base(storage_uuid), params);
  return assertOk<GetStoragePhotosResult>(res, "Failed to load photos");
};

const startUpload = async (
  storage_uuid: string,
  data: StartUploadBody
): Promise<StartUploadResult> => {
  const res = await httpPost(`${base(storage_uuid)}/start-upload`, data);
  const ok = assertOk<StartUploadResult>(res, "Failed to start photo upload");
  if (!ok.data.uploadId || !Array.isArray(ok.data.signedUrls)) {
    throw new Error("Failed to start photo upload");
  }
  return ok;
};

const completeUpload = async (
  storage_uuid: string,
  photo_uuid: string,
  body: CompleteUploadBody
): Promise<CompleteUploadResult> => {
  const res = await httpPost(
    `${base(storage_uuid)}/${photo_uuid}/complete-upload`,
    body
  );
  return assertOk<CompleteUploadResult>(res, "Failed to complete photo upload");
};

const deletePhoto = async ({
  storage_uuid,
  photo_uuid,
}: DeleteStoragePhotoData): Promise<void> => {
  const res = await httpDelete(`${base(storage_uuid)}/${photo_uuid}`);
  assertOk(res, "Failed to delete photo", false);
};

/**
 * Routes for the shared Uploader. No `markFailed`: the storage routes have no
 * PATCH, and the Uploader skips it when `startUpload` is custom.
 */
const uploaderApi = (storage_uuid: string): UploaderApi => ({
  startUpload: ({ uuid, parts, file }) =>
    startUpload(storage_uuid, {
      uuid,
      name: file.name,
      size: file.size as number,
      // The Uploader passes its own parts, { PartNumber, ETag: "" }; the API
      // schema is strict per part and rejects the extra ETag key.
      parts: parts.map(({ PartNumber }) => ({ PartNumber })),
      client_modified: file.client_modified,
    }),
  completeUpload: ({ uuid, uploadId, parts, md5, sha1, sha256 }) =>
    completeUpload(storage_uuid, uuid, {
      uploadId,
      parts,
      md5,
      sha1,
      sha256,
    }),
});

export const StoragePhotosAPI = {
  get,
  startUpload,
  completeUpload,
  delete: deletePhoto,
  uploaderApi,
};
